import { unzip, strFromU8 } from 'fflate';
import { TGALoader } from 'three/examples/jsm/loaders/TGALoader.js';
import { educationRegistry, educationTextureFile, resolveEducationAppearance, type EducationCatalog, type EducationRegistry } from './educationCatalog';
import { appearanceKey } from './javaModels';
import { blockAppearance, type BlockAppearance } from './blockAppearance';
import { appearanceTextureNames, type ResourcePackTextures } from '../render/structureAssets';
import { decodeTexture, type RawResourcePack } from './resourcePack';
import type { PaletteEntry } from '../nbt/types';
import type { SourceTexture } from './atlas';
import { educationItemTexture, registerEducationNames } from './educationMaterials';

const PACK_URL='/minecraft/education-1.26.32.zip';
export interface LoadedEducationPack { registry: EducationRegistry; files: Record<string,Uint8Array>; decoded: Map<string,SourceTexture> }
let standardPromise:Promise<LoadedEducationPack>|undefined;
export function loadEducationPack():Promise<LoadedEducationPack> {
  if(!standardPromise) standardPromise=(async()=> {
    const response=await fetch(PACK_URL,{signal:AbortSignal.timeout(20000)});
    if(!response.ok)throw new Error('教育版の見た目データを読み込めませんでした。再読み込みしてください。');
    const bytes=new Uint8Array(await response.arrayBuffer());
    const files=await new Promise<Record<string,Uint8Array>>((resolve,reject)=>unzip(bytes,(error,result)=>error?reject(error):resolve(result)));
    const catalog=JSON.parse(strFromU8(files['catalog.json'])) as EducationCatalog;
    registerEducationNames(catalog);
    return {registry:educationRegistry(catalog),files,decoded:new Map<string,SourceTexture>()};
  })().catch(error=>{standardPromise=undefined;throw error;});
  return standardPromise;
}
async function sourceTexture(pack:LoadedEducationPack,id:string,custom?:RawResourcePack|null):Promise<SourceTexture|null> {
  // Custom Java textures override only supplied files. Missing files retain the standard model/texture.
  const key=id.replace(/^minecraft:block\//,'');
  const customBytes=custom?.files.get(id)??custom?.files.get(key);
  if(customBytes){const decoded=await decodeTexture(customBytes);if(decoded)return decoded;}
  if(pack.decoded.has(id))return pack.decoded.get(id)!;
  const file=educationTextureFile(pack.registry,id);
  if(!file)return null;
  let texture:SourceTexture|null;
  if(file==='viewer:blackboard')texture={size:1,data:new Uint8ClampedArray([28,35,32,255])};
  else {
    const bytes=pack.files[file];if(!bytes)return null;
    if(file.endsWith('.tga')) {
      const decoded=new TGALoader().parse(bytes.slice().buffer);
      if(!decoded.width||!decoded.data)return null;
      texture={size:decoded.width,data:new Uint8ClampedArray(decoded.data.slice(0,decoded.width**2*4))};
    } else texture=await decodeTexture(bytes);
  }
  if(texture)pack.decoded.set(id,texture);
  return texture;
}
export async function prepareEducationAssets(entries:Pick<PaletteEntry,'name'|'properties'>[],custom?:RawResourcePack|null,materials:string[]=[]):Promise<ResourcePackTextures> {
  const pack=await loadEducationPack();
  const appearances=new Map<string,BlockAppearance>(),textures=new Map<string,SourceTexture>(),unsupported=new Set<string>();
  for(const entry of entries) {
    const key=appearanceKey(entry.name,entry.properties);
    if(appearances.has(key))continue;
    let appearance:BlockAppearance|null=null;
    try{appearance=resolveEducationAppearance(pack.registry,entry.name,entry.properties);}catch{ /* Display missing definition explicitly below. */ }
    if(!appearance) {unsupported.add(entry.name);appearance=blockAppearance(entry.name,entry.properties);}
    appearances.set(key,appearance);
  }
  const previews=new Map<string,BlockAppearance>(),itemIcons=new Map<string,string>();
  for(const name of new Set([...entries.map(e=>e.name),...materials])) {
    const props:Record<string,string>={facing:'south',rotation:'8',ground_sign_direction:'8',east:'true',west:'true'};
    if(name.endsWith('_door'))props.half='lower';
    let preview=resolveEducationAppearance(pack.registry,name,props);
    if(preview && name.endsWith('_door')) {
      const upper=resolveEducationAppearance(pack.registry,name,{...props,half:'upper'});
      if(upper?.quads) preview={...preview,quads:[...preview.quads??[],...upper.quads.map(q=>({...q,corners:q.corners.map((v,i)=>v+(i%3===1?1:0))}))]};
    }
    if(preview)previews.set(name,preview);
    else if(materials.includes(name)) {
      const icon=educationItemTexture(pack.registry,name);
      if(icon)itemIcons.set(name,icon);
      else unsupported.add(name);
    }
  }
  const names=new Set([...appearances.values(),...previews.values()].flatMap(appearanceTextureNames));
  itemIcons.forEach(texture=>names.add(texture));
  await Promise.all([...names].map(async name=>{const texture=await sourceTexture(pack,name,custom);if(texture)textures.set(name,texture);}));
  return {name:`Minecraft Education ${pack.registry.catalog.educationVersion}`,tile:Math.max(16,...[...textures.values()].map(t=>t.size)),textures,appearances,previews,itemIcons,unsupported:[...unsupported],catalogSize:Object.keys(pack.registry.catalog.blocks).length};
}
